/* Online storefront guard. The 3PL remains the owner of its Shopify inventory. */
(function () {
  'use strict';
  const registry = new Map();
  const nativeFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';
  const message = () => window.theme?.strings?.products_variant_no_stock || 'Agotado. Revisa los productos y las tallas seleccionadas.';

  function product(value) {
    if (value && typeof value.bss_available === 'boolean') {
      value.available = value.bss_available;
      value.variants.forEach(v => { v.available = value.bss_variants[String(v.id)] === true; });
    }
    return value;
  }

  function register(data) {
    if (!data || !Array.isArray(data.variants) || !data.handle) throw new Error(message());
    data.variants.forEach(v => registry.set(String(v.id), { ...v, handle: data.handle, pack: data.pack === true, components: data.components }));
  }

  function readPage() {
    document.querySelectorAll('script.bss-stock-data:not([data-bss-read])').forEach(el => {
      try { register(JSON.parse(el.textContent)); el.dataset.bssRead = 'true'; } catch (_) { /* Missing data is rejected below. */ }
    });
  }

  function idsFor(item) {
    if (Number(item.quantity ?? 1) <= 0) return [];
    const id = String(item.id).split('/').pop();
    const selection = item.properties?._bundle_selection;
    if (selection && registry.get(id)?.pack) {
      const ids = JSON.parse(selection);
      if (!Array.isArray(ids) || !ids.length) throw new Error(message());
      return ids.map(value => String(value).split('/').pop());
    }
    return [id];
  }

  async function checkItems(items) {
    if (!window.bssBigblueEnabled) return;
    readPage();
    const ids = items.flatMap(idsFor);
    const ownIds = items.filter(item => Number(item.quantity ?? 1) > 0).map(item => String(item.id).split('/').pop());
    const handles = new Set([...ids, ...ownIds].map(id => {
      const entry = registry.get(id);
      if (!entry) throw new Error(message());
      return entry.handle;
    }));
    // Re-read the Liquid snapshot for already-open pages and previously selected pack components.
    const fresh = new Map();
    await Promise.all([...handles].map(async handle => {
      const response = await nativeFetch(`${root()}products/${encodeURIComponent(handle)}?view=bss-stock&_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(message());
      const data = await response.json();
      if (data.handle !== handle || !Array.isArray(data.variants)) throw new Error(message());
      register(data);
      data.variants.forEach(v => fresh.set(String(v.id), v.available === true));
    }));
    if (ids.some(id => fresh.get(id) !== true)) throw new Error(message());
    await checkQuantities(items);
  }

  // Available is not enough: the cart may already use the units (same component in another pack,
  // loose line + pack, pack quantity > stock). Plan the cart with the new items exactly as
  // bss-cart-stock.js will, and refuse the add instead of letting the cart remove it afterwards.
  async function checkQuantities(items) {
    const cartStock = window.BuddhaCartStock;
    if (!cartStock) {
      if (items.some(item => item.properties?._bundle_selection)) throw new Error(message());
      return;
    }
    const data = await cartStock.fetchStock();
    const added = items.filter(item => Number(item.quantity ?? 1) > 0).map((item, index) => {
      const id = String(item.id).split('/').pop();
      const entry = registry.get(id);
      const line = { key: `bss-new-${index}`, variant_id: Number(id), quantity: Number(item.quantity ?? 1), limit: entry.limit ?? -1 };
      const selection = item.properties?._bundle_selection;
      if (selection && entry.pack) Object.assign(line, { bundle_selection: selection, components: entry.components });
      return line;
    });
    const before = new Map(cartStock.plan(data.lines).map(change => [change.key, change.newQuantity]));
    const after = cartStock.plan([...data.lines, ...added]);
    if (after.some(change => before.get(change.key) !== change.newQuantity)) throw new Error(message());
  }

  function showError() {
    let notice = document.getElementById('bss-stock-error');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'bss-stock-error';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:10000;background:#fff;color:#a40000;padding:16px 24px;border:1px solid currentColor;max-width:90vw;box-shadow:0 2px 12px #0003';
      document.body.appendChild(notice);
    }
    notice.textContent = message();
    clearTimeout(notice.hideTimer);
    notice.hideTimer = setTimeout(() => notice.remove(), 7000);
  }

  function decode(body) {
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_) { body = new URLSearchParams(body); }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const item = { id: body.get('id'), quantity: body.get('quantity') || 1, properties: {} };
      for (const [name, value] of body.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match) item.properties[match[1]] = value;
      }
      return item;
    }
    return body;
  }

  window.BuddhaStock = {
    product,
    products: values => values?.map(product) ?? null,
    checkItems,
    // Preserve the theme's jQuery completion/error handlers with the guarded fetch path.
    post: ($, url, body, success) => {
      const deferred = $.Deferred();
      window.fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
        .then(async response => {
          const data = await response.json();
          if (!response.ok) { deferred.reject({ status: response.status, responseText: JSON.stringify(data) }); return; }
          success(data);
          deferred.resolve(data);
        }).catch(() => deferred.reject({ status: 503, responseText: JSON.stringify({ description: message() }) }));
      return deferred.promise();
    }
  };

  if (!window.bssBigblueEnabled) return;

  window.fetch = async function (input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    if (url.origin === location.origin && /\/cart\/add(?:\.js)?$/.test(url.pathname) && method === 'POST') {
      try {
        const body = init?.body ?? (input instanceof Request ? await input.clone().text() : null);
        const data = decode(body);
        if (!data) throw new Error(message());
        await checkItems(data.items || [data]);
      } catch (_) {
        showError();
        return new Response(JSON.stringify({ status: 422, message: message(), description: message() }), { status: 422, headers: { 'Content-Type': 'application/json' } });
      }
    }
    return nativeFetch(input, init);
  };

  const approved = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add(?:\.js)?$/.test(new URL(form.action, location.origin).pathname)) return;
    if (approved.has(form)) { approved.delete(form); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await checkItems([decode(new FormData(form))]);
      approved.add(form);
      form.requestSubmit(event.submitter || undefined);
      approved.delete(form);
    } catch (_) { showError(); }
  }, true);
})();
